/**
 * Helper centralizado para los sonidos de notificación del chat de soporte
 * (consola superadmin). Espejo de apps/web/src/lib/supportAudio.ts
 */

/** Versión de assets de audio; subir al cambiar los WAV en public/sounds. */
const SOUND_VERSION = "2";

const SOUNDS = {
  new: `/sounds/support-new-message.wav?v=${SOUND_VERSION}`,
  add: `/sounds/support-add-message.wav?v=${SOUND_VERSION}`,
} as const;

export type SupportSoundKind = keyof typeof SOUNDS;

const MUTE_KEY = "redipos.support.audio.muted.v1";

type MuteState = Record<SupportSoundKind, boolean>;

const DEFAULT_MUTE: MuteState = { new: false, add: false };

function readMute(): MuteState {
  if (typeof localStorage === "undefined") return DEFAULT_MUTE;
  try {
    const raw = localStorage.getItem(MUTE_KEY);
    if (!raw) return DEFAULT_MUTE;
    const parsed = JSON.parse(raw) as Partial<MuteState>;
    return {
      new: Boolean(parsed?.new),
      add: Boolean(parsed?.add),
    };
  } catch {
    return DEFAULT_MUTE;
  }
}

function writeMute(next: MuteState): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(MUTE_KEY, JSON.stringify(next));
  } catch {
    /* almacenamiento lleno / bloqueado: ignorar */
  }
}

const cache: Record<SupportSoundKind, HTMLAudioElement | null> = {
  new: null,
  add: null,
};

let audioUnlocked = false;

function getAudio(kind: SupportSoundKind): HTMLAudioElement {
  if (!cache[kind]) {
    const a = new Audio(SOUNDS[kind]);
    a.preload = "auto";
    a.volume = 0.6;
    cache[kind] = a;
  }
  return cache[kind] as HTMLAudioElement;
}

/** Desbloquea autoplay tras la primera interacción del usuario (click/tecla). */
export function unlockSupportAudio(): void {
  if (audioUnlocked || typeof window === "undefined") return;
  audioUnlocked = true;
  for (const kind of Object.keys(SOUNDS) as SupportSoundKind[]) {
    const a = getAudio(kind);
    a.play()
      .then(() => {
        a.pause();
        try {
          a.currentTime = 0;
        } catch {
          /* noop */
        }
      })
      .catch(() => undefined);
  }
}

function ensureAudioUnlockListeners(): void {
  if (typeof window === "undefined" || audioUnlocked) return;
  const once = (): void => {
    unlockSupportAudio();
    window.removeEventListener("pointerdown", once);
    window.removeEventListener("keydown", once);
  };
  window.addEventListener("pointerdown", once, { once: true, passive: true });
  window.addEventListener("keydown", once, { once: true });
}

if (typeof window !== "undefined") {
  ensureAudioUnlockListeners();
}

function isTabActive(): boolean {
  if (typeof document === "undefined") return false;
  return document.visibilityState === "visible" && document.hasFocus();
}

export function playSupportSound(
  kind: SupportSoundKind,
  { onlyIfHidden }: { onlyIfHidden?: boolean } = { onlyIfHidden: true },
): void {
  if (isMuted(kind)) return;
  if (onlyIfHidden && isTabActive()) return;
  ensureAudioUnlockListeners();
  const a = getAudio(kind);
  try {
    a.currentTime = 0;
  } catch {
    /* si el browser todavía no cargó metadata, no pasa nada */
  }
  a.play().catch(() => undefined);
}

export function isMuted(kind: SupportSoundKind): boolean {
  return readMute()[kind];
}

export function setMuted(kind: SupportSoundKind, muted: boolean): void {
  const next: MuteState = { ...readMute(), [kind]: muted };
  writeMute(next);
}

export function isAnyMuted(): boolean {
  const m = readMute();
  return m.new || m.add;
}

export function unmuteSupportAudio(kind: SupportSoundKind): void {
  setMuted(kind, false);
}

export function previewSupportSound(kind: SupportSoundKind): void {
  const a = getAudio(kind);
  try {
    a.currentTime = 0;
  } catch {
    /* noop */
  }
  a.play().catch(() => undefined);
}
